import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, resolveDirective as _resolveDirective, withDirectives as _withDirectives, createBlock as _createBlock } from "vue"

const _hoisted_1 = ["innerHTML", "data-id"]
const _hoisted_2 = ["data-id"]
const _hoisted_3 = { class: "product-element__container q-card q-h-100 q-p-3 q-shadow-sm q-rounded-3 q-position-relative q-bg-white q-overflow-hidden" }
const _hoisted_4 = {
  key: 0,
  class: "product-element__discount q-text-center q-text-white q-position-absolute q-discount q-p-1 q-bg-danger"
}
const _hoisted_5 = { class: "q-discount-discount" }
const _hoisted_6 = { class: "product-element__image-container q-card-image" }
const _hoisted_7 = ["alt"]
const _hoisted_8 = { class: "product-element__info-box q-card-body" }
const _hoisted_9 = { class: "product-element__title q-mb-3 q-lh-base" }
const _hoisted_10 = ["href", "target"]

import AutocompletePriceBox from '@/components/AutocompletePopup/AutocompletePriceBox.vue'
import {computed, inject, nextTick, onMounted, onUpdated, ref, toRefs, watch} from 'vue';

import {addDisplayMacros, formatPrice, generateStaticImage, getDiscount, getRealPrice, parsePrice} from '@/core/utils';
import {createTemplate, validateTemplate} from "@/core/Template";
import {SmartSearchConfigInterface} from '@/core/types';

// eslint-disable-next-line @typescript-eslint/no-non-null-assertion

export default /*@__PURE__*/_defineComponent({
  __name: 'ProductCard',
  props: ['product', 'component'],
  setup(__props) {

const SmartSearchConfig = ref(inject<SmartSearchConfigInterface>('SmartSearchConfig')!);

const {smallDevice, mediumDevice, largeDevice, extraLargeDevice} = SmartSearchConfig.value.serp.gridSettings.column;
const useStylisationUrl = !!SmartSearchConfig.value.appConfiguration.stylizationUrl;

const computedClass = computed(() => {
  return [
    `q-g-col-sm-${12 / smallDevice}`,
    `q-g-col-md-${12 / mediumDevice}`,
    `q-g-col-lg-${12 / largeDevice}`,
    `q-g-col-xl-${12 / extraLargeDevice}`,
  ];
});

const {currency: currencyCode} = SmartSearchConfig.value.appConfiguration;

const props = __props;
const {id, link, title, link_click, variants} = toRefs(props.product);

const firstVariant = computed(() => variants.value[0]);

const priceData = computed(() => {
  const customMapping = window._QONizer?.customSmartSearchConfig?.customPriceMapping;
  
  const priceKey = customMapping?.price || 'price';
  const oldPriceKey = customMapping?.oldPrice || 'old_price';
  const salePriceKey = customMapping?.salePrice || 'sale_price';

  const v = firstVariant.value;
  const rawPrice = v[priceKey];
  const rawOldPrice = v[oldPriceKey];
  const rawSalePrice = v[salePriceKey];

  const priceToParse = (typeof rawPrice === 'string' || typeof rawPrice === 'number') ? rawPrice : v.price;
  const oldPriceToParse = (typeof rawOldPrice === 'string' || typeof rawOldPrice === 'number') ? rawOldPrice : v.old_price;
  const salePriceToParse = (typeof rawSalePrice === 'string' || typeof rawSalePrice === 'number') ? rawSalePrice : v.sale_price;

  const secondPrice = (oldPriceToParse || salePriceToParse) as string | number | null;
  const parsedPrice = parsePrice(priceToParse as string | number | null);
  const parsedOldPrice = parsePrice(secondPrice);
  
  // getRealPrice swaps if parsedPrice > parsedOldPrice
  const realPrices = getRealPrice(parsedPrice, parsedOldPrice);
  
  return {
    price: realPrices.price,
    oldPrice: realPrices.oldPrice,
    parsedPrice, // original price from feed
    parsedOldPrice // original old price from feed
  };
});

const discountValue = computed(() => {
  const { price, oldPrice, parsedPrice, parsedOldPrice } = priceData.value;
  
  // SAFETY: If the original price from feed (parsedPrice) is NOT lower than the original old price (parsedOldPrice),
  // then it's not a discount (it might be a swap or a price increase).
  // We only want to show a discount if there is a real reduction.
  if (parsedPrice && parsedOldPrice && parsedPrice >= parsedOldPrice) {
    return null;
  }
  
  return getDiscount(price, oldPrice);
});

const discount = computed(() => discountValue.value);

const omnibusPrice = computed(() => parsePrice(firstVariant.value.omnibus_price));

const {width, height, useQuarticImages} = SmartSearchConfig.value.serp.productImageSize;
const {removeDecimals} = SmartSearchConfig.value.appConfiguration;

const staticImage = computed(() => {
  const imageLink = firstVariant.value.image_link
  if (!useQuarticImages) {
    return imageLink
  }
  return imageLink ? generateStaticImage(imageLink, width, height) : generateStaticImage('https://static.quartic.com.pl/img/no-image/no-image.png', width, height);
})

const formattedPrice = computed(() => formatPrice(priceData.value.price, currencyCode, removeDecimals));
const formattedPriceOld = computed(() => formatPrice(priceData.value.oldPrice, currencyCode, removeDecimals));
const formattedOmnibusPrice = computed(() => formatPrice(omnibusPrice.value, currencyCode, removeDecimals));

const actualLink = ref(link.value);

const repairLink = (link: string) => link.replace(/^http:/, 'https:');

const handleClick = () => {
  if (useStylisationUrl) {
    actualLink.value = `${SmartSearchConfig.value.appConfiguration.stylizationUrl}/${id.value}`;
    return;
  }
  actualLink.value = repairLink(link_click.value);
};

const prepareClearLink = (link: string, clickLink: string) => {
  return ` href="${link}" data-href="${repairLink(clickLink)}" onmousedown="return this.href = this.getAttribute('data-href');" ontouchstart="return this.href = this.getAttribute('data-href');" `;
};

const generatedHtml = computed(() => {
  if (!props.component) return '';

  const v = { ...firstVariant.value };
  v.discount = discount.value ? `- ${discount.value}%` : null;
  v.image_link_cdn = staticImage.value;
  v.price_formatted = formattedPrice.value;
  v.price_number = priceData.value.price;
  v.old_price_formatted = formattedPriceOld.value;
  v.old_price_number = priceData.value.oldPrice;
  v.omnibus_price_formatted = formattedOmnibusPrice.value;
  v.omnibus_price_number = omnibusPrice.value;
  v.id = id.value;
  v.link_click = link_click.value;
  v.clear_link = prepareClearLink(link.value, link_click.value);
  addDisplayMacros(v);
  
  return createTemplate(props.component, v, variants.value);
});


onUpdated(() => {
  if (!props.component) return;

  const container = document.querySelector('.product-card-parent');
  if (!container) return;

  if (window._QONizer?.onProductMounted) {
    try {
      const plainProduct = JSON.parse(JSON.stringify(props.product));
      window._QONizer.onProductMounted(plainProduct);
    } catch (e) {
      console.warn('Błąd przy przetwarzaniu props.product dla _QONizer:', e);
    }
  }
  if (window._QONizer?.customSmartSearchConfig?.onProductMounted) {
    try {
      const plainProduct = JSON.parse(JSON.stringify(props.product));
      window._QONizer?.customSmartSearchConfig?.onProductMounted(plainProduct);
    } catch (e) {
      console.warn('Błąd przy przetwarzaniu props.product dla _QONizer:', e);
    }
  }

});


function fireQonizer() {
  if (!props.component) return;

  nextTick(() => {
    const el = document.querySelector(
        `.product-card-parent[data-id="${id.value}"]`
    );
    if (!el) return;
    if (window._QONizer?.onProductMounted) {
      try {
        window._QONizer?.onProductMounted?.(JSON.parse(JSON.stringify(props.product)));
      } catch (e) {
        console.warn('onProductMounted error:', e);
      }
    }

    if (window._QONizer?.customSmartSearchConfig?.onProductMounted) {
      try {
        const plainProduct = JSON.parse(JSON.stringify(props.product));
        window._QONizer?.customSmartSearchConfig?.onProductMounted(plainProduct);
      } catch (e) {
        console.warn('Błąd przy przetwarzaniu props.product dla _QONizer:', e);
      }
    }
  });
}

onMounted(fireQonizer);

watch(
    () => [generatedHtml, props.product, props.component],
    fireQonizer,
    {deep: true, immediate: false}
);



return (_ctx: any,_cache: any) => {
  const _directive_lazy = _resolveDirective("lazy")!

  return (__props.component)
    ? (_openBlock(), _createElementBlock("div", {
        key: 0,
        class: _normalizeClass(["product-card-parent q-p-1 q-p-sm-2", computedClass.value]),
        innerHTML: generatedHtml.value,
        "data-id": _unref(id)
      }, null, 10, _hoisted_1))
    : (_openBlock(), _createElementBlock("div", {
        key: 1,
        class: _normalizeClass(["product-element q-p-1 q-p-sm-2", computedClass.value]),
        "data-id": _unref(id)
      }, [
        _createElementVNode("div", _hoisted_3, [
          (discount.value)
            ? (_openBlock(), _createElementBlock("span", _hoisted_4, [
                _cache[0] || (_cache[0] = _createElementVNode("span", { class: "q-discount-minus" }, "-", -1)),
                _createElementVNode("span", _hoisted_5, _toDisplayString(discount.value), 1),
                _cache[1] || (_cache[1] = _createElementVNode("span", { class: "q-percentage" }, "%", -1))
              ]))
            : _createCommentVNode("", true),
          _createElementVNode("div", _hoisted_6, [
            _withDirectives(_createElementVNode("img", {
              class: "product-element__image q-mx-auto q-d-block q-card-img-top q-img-fluid",
              alt: _unref(title)
            }, null, 8, _hoisted_7), [
              [_directive_lazy, staticImage.value]
            ])
          ]),
          _createElementVNode("div", _hoisted_8, [
            _createElementVNode("h6", _hoisted_9, _toDisplayString(_unref(title)), 1),
            (!useStylisationUrl)
              ? (_openBlock(), _createBlock(AutocompletePriceBox, {
                  key: 0,
                  product: firstVariant.value
                }, null, 8, ["product"]))
              : _createCommentVNode("", true)
          ]),
          _createElementVNode("a", {
            class: "product-element__link q-stretched-link",
            href: actualLink.value,
            onClick: handleClick,
            target: useStylisationUrl ? '_blank' : '_self'
          }, null, 8, _hoisted_10)
        ])
      ], 10, _hoisted_2))
}
}

})